import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, FileText, Loader2, RefreshCw } from 'lucide-react';
import api, { FormLeadFieldMapping, FormLeadMessageConfig, FormLeadTemplateBinding, listWhatsAppTemplates, WhatsAppTemplate } from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { getDashboardThemeClasses } from './dashboard/dashboardTheme.ts';
import WhatsAppTemplatePreview from './whatsapp-templates/WhatsAppTemplatePreview.tsx';

export type FormOfficialMessageConfig = FormLeadMessageConfig;

export interface FormPayloadOption { path: string; label: string; value: string; sourceLabel?: string }
interface TemplateSlot {
  component: 'header' | 'body' | 'button';
  parameter: string;
  button_index?: number;
  label: string;
  media?: string;
  literalOnly?: boolean;
  maxLength?: number;
}
export interface FormTemplateCatalog {
  clinicId: number | null;
  isOfficial: boolean | null;
  loading: boolean;
  error: string;
  templates: WhatsAppTemplate[];
  reload: () => void;
}

const slotKey = (slot: TemplateSlot) =>
  slot.component === 'button' ? `button.${slot.button_index}.${slot.parameter}` : `${slot.component}.${slot.parameter}`;
const placeholders = (text: unknown): string[] => Array.from(new Set(
  Array.from(String(text || '').matchAll(/{{([1-9]\d*)}}/g), match => match[1]),
)).sort((a, b) => Number(a) - Number(b));

export function analyzeFormTemplate(template: WhatsAppTemplate): { slots: TemplateSlot[]; errors: string[] } {
  const slots: TemplateSlot[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();
  if (!['UTILITY', 'MARKETING'].includes(template.category)) errors.push('Este tipo de template não é compatível com mensagens de formulário.');
  if (!Array.isArray(template.components) || !template.components.some(c => c?.type === 'BODY' && typeof c.text === 'string' && c.text.trim())) {
    return { slots, errors: ['Este template não possui um corpo de mensagem válido.'] };
  }
  const checkText = (text: unknown) => {
    const value = String(text || '');
    const positions = placeholders(value);
    if (/{{|}}/.test(value.replace(/{{([1-9]\d*)}}/g, '')) || positions.some((p, i) => Number(p) !== i + 1)) {
      errors.push('Formulários aceitam variáveis numéricas sequenciais: {{1}}, {{2}}.');
    }
    return positions;
  };
  template.components.forEach(component => {
    if (!component || seen.has(component.type)) { errors.push('Componentes de template inválidos.'); return; }
    seen.add(component.type);
    if (component.type === 'BODY' || (component.type === 'HEADER' && (component.format || 'TEXT') === 'TEXT')) {
      const positions = checkText(component.text);
      if (component.type === 'HEADER' && positions.length > 1) errors.push('O cabeçalho aceita somente uma variável.');
      positions.forEach(parameter => slots.push({
        component: component.type === 'BODY' ? 'body' : 'header', parameter,
        label: `${component.type === 'BODY' ? 'Mensagem' : 'Cabeçalho'} · {{${parameter}}}`,
      }));
    } else if (component.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)) {
      slots.push({ component: 'header', parameter: 'media', media: component.format,
        label: `Cabeçalho · ${({ IMAGE: 'imagem', VIDEO: 'vídeo', DOCUMENT: 'documento' } as Record<string, string>)[component.format]}` });
    } else if (component.type === 'BUTTONS') {
      if (!Array.isArray(component.buttons) || !component.buttons.length || component.buttons.length > 10) { errors.push('Botões de template inválidos.'); return; }
      component.buttons.forEach((button: any, index: number) => {
        if (!button) { errors.push('Botão de template inválido.'); return; }
        if (button.type === 'URL') {
          const positions = checkText(button.url);
          if (positions.length && (positions.length !== 1 || !String(button.url).endsWith('{{1}}'))) errors.push('O botão de URL aceita uma variável ao final do endereço.');
          positions.forEach(parameter => slots.push({ component: 'button', parameter, button_index: index,
            label: `Botão ${index + 1} · ${button.text} · {{${parameter}}}` }));
        } else if (button.type === 'COPY_CODE') {
          slots.push({ component: 'button', parameter: '1', button_index: index, literalOnly: true, maxLength: 15,
            label: `Botão ${index + 1} · ${button.text} · código` });
        } else if (!['PHONE_NUMBER', 'QUICK_REPLY'].includes(button.type)) {
          errors.push(`O botão ${button.text || index + 1} ainda não é compatível com envios de formulário.`);
        }
      });
    } else if (component.type === 'FOOTER') {
      if (checkText(component.text).length) errors.push('O rodapé não aceita variáveis.');
    } else {
      errors.push('Este template usa um componente ainda não compatível com envios de formulário.');
    }
  });
  return { slots, errors: Array.from(new Set(errors)) };
}

export function useFormOfficialTemplates(clinicId: number | null): FormTemplateCatalog {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<Omit<FormTemplateCatalog, 'reload'>>({ clinicId: null, isOfficial: null, loading: true, error: '', templates: [] });
  const requestId = useRef(0);
  const reload = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    const request = ++requestId.current;
    let cancelled = false;
    setState({ clinicId, isOfficial: null, loading: true, error: '', templates: [] });
    if (!clinicId) {
      setState({ clinicId, isOfficial: null, loading: false, error: 'Selecione uma clínica para verificar o canal de envio.', templates: [] });
      return;
    }
    let official: boolean | null = null;
    const load = async () => {
      try {
        // The legacy provider helper masks errors as WAHA; this configuration must fail closed.
        const { data } = await api.get(`/api/clinics/${clinicId}/whatsapp-providers`);
        if (typeof data?.has_official !== 'boolean') throw new Error('Invalid channel response');
        official = data.has_official;
        const templates = official ? await listWhatsAppTemplates(clinicId, 'APPROVED', 'form') : [];
        if (!cancelled && request === requestId.current) setState({ clinicId, isOfficial: official, loading: false, error: '',
          templates: templates.filter(template => template.status === 'APPROVED' && template.use_case === 'form'
            && (!('clinic_id' in template) || Number((template as any).clinic_id) === clinicId)),
        });
      } catch {
        if (!cancelled && request === requestId.current) setState({ clinicId, isOfficial: official, loading: false, templates: [],
          error: official ? 'Não foi possível carregar os templates aprovados desta clínica.' : 'Não foi possível verificar o canal de WhatsApp desta clínica.',
        });
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [clinicId, revision]);
  return state.clinicId === clinicId ? { ...state, reload } : { clinicId, isOfficial: null, loading: true, error: '', templates: [], reload };
}

export function validateFormOfficialTemplate(config: FormOfficialMessageConfig, catalog: FormTemplateCatalog, mapping?: FormLeadFieldMapping): string | null {
  if (!config.send_initial_message) return null;
  if (catalog.loading || catalog.isOfficial === null) return catalog.error || 'Aguarde a verificação do canal de WhatsApp.';
  if (catalog.error) return catalog.error;
  if (!catalog.isOfficial) return config.official_template_id ? 'O template salvo exige uma conexão oficial disponível. Revise o canal antes de ativar o envio.' : null;
  const template = catalog.templates.find(item => item.id === config.official_template_id);
  if (!template) return 'Selecione um template aprovado para formulários antes de ativar o envio inicial.';
  const { slots, errors } = analyzeFormTemplate(template);
  if (errors.length) return errors[0];
  if (Object.keys(config.official_template_parameters || {}).some(key => !slots.some(slot => slotKey(slot) === key))) return 'O template mudou. Selecione-o novamente para revisar suas variáveis.';
  for (const slot of slots) {
    const binding = config.official_template_parameters?.[slotKey(slot)];
    if (!binding || !['literal', 'contact_first_name', 'contact_full_name', 'clinic_name', 'interest', 'form_message'].includes(binding.source)) {
      return `Defina o valor de ${slot.label}.`;
    }
    if (binding.source === 'literal' && !binding.value?.trim()) return `Preencha ${slot.label}.`;
    if ((slot.media || slot.literalOnly) && binding.source !== 'literal') return `Informe um valor fixo para ${slot.label}.`;
    if (mapping && (binding.source === 'interest' || binding.source === 'form_message') && !mapping[binding.source === 'interest' ? 'interest' : 'message']?.trim()) return `Mapeie o campo do formulário usado em ${slot.label}.`;
    if (binding.source === 'literal' && !slot.media && ((binding.value || '').length > (slot.maxLength || 1024) || /[\n\r\t]/.test(binding.value || ''))) return `Revise o valor de ${slot.label}: até ${slot.maxLength || 1024} caracteres, sem quebras de linha.`;
    if (slot.media && binding.source === 'literal') {
      try {
        const url = new URL(binding.value || '');
        if (url.protocol !== 'https:' || url.username || url.password || url.hash || url.port || !url.hostname.includes('.') || /(^127\.|^10\.|^192\.168\.|^169\.254\.|^0\.|^172\.(1[6-9]|2\d|3[01])\.|\.(local|localhost|internal|test)$)/.test(url.hostname) || /[\s\\]/.test(binding.value || '')) return 'A mídia deve usar uma URL HTTPS pública, sem credenciais.';
      } catch { return 'A mídia deve usar uma URL HTTPS pública válida.'; }
    }
  }
  return null;
}

interface Props {
  catalog: FormTemplateCatalog;
  config: FormOfficialMessageConfig;
  onChange: (config: FormOfficialMessageConfig) => void;
  payloadOptions: FormPayloadOption[];
  fieldMapping: FormLeadFieldMapping;
  onFieldMappingChange: (mapping: FormLeadFieldMapping) => void;
  disabled?: boolean;
}

export default function FormOfficialTemplateSettings({ catalog, config, onChange, payloadOptions, fieldMapping, onFieldMappingChange, disabled = false }: Props) {
  const { isDark } = useTheme();
  const selected = catalog.templates.find(item => item.id === config.official_template_id);
  const analysis = useMemo(() => selected ? analyzeFormTemplate(selected) : { slots: [], errors: [] }, [selected]);
  const previewValue = (slot: TemplateSlot) => {
    const binding = config.official_template_parameters?.[slotKey(slot)];
    if (!binding) return `{{${slot.parameter}}}`;
    if (binding.source === 'literal') return binding.value || `{{${slot.parameter}}}`;
    return ({ contact_first_name: 'Marina', contact_full_name: 'Marina Silva', clinic_name: 'Sua clínica', interest: '[Interesse do lead]', form_message: '[Mensagem do formulário]' } as Record<string, string>)[binding.source];
  };
  const theme = getDashboardThemeClasses(isDark);
  const muted = theme.mutedText;
  const field = `min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm focus:border-sminy-primary focus:outline-none focus:ring-2 focus:ring-sminy-primary/20 disabled:opacity-60 ${theme.input}`;
  const updateBinding = (slot: TemplateSlot, patch: Partial<FormLeadTemplateBinding>) => {
    const bindings = config.official_template_parameters || {};
    const next: FormLeadTemplateBinding = { source: 'literal', value: '', ...bindings[slotKey(slot)], ...patch };
    onChange({ ...config, official_template_parameters: { ...bindings, [slotKey(slot)]: next } });
  };
  if (catalog.loading) return <p role="status" className={`mt-4 flex items-center gap-2 text-sm ${muted}`}><Loader2 className="h-4 w-4 animate-spin" />Verificando canal e templates…</p>;
  if (catalog.error) return <div role="alert" className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm"><p>{catalog.error}</p><p className={`mt-1 ${muted}`}>Você pode desativar a mensagem inicial para integrar somente ao CRM.</p><button type="button" onClick={catalog.reload} className="mt-3 inline-flex min-h-[44px] items-center gap-2 text-sminy-primary"><RefreshCw className="h-4 w-4" />Tentar novamente</button></div>;
  if (!catalog.isOfficial) return config.official_template_id ? <div role="alert" className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm"><p>O template salvo exige uma conexão oficial disponível. A seleção foi preservada e não será substituída pelo agente.</p><p className={`mt-1 ${muted}`}>Revise o canal ou desative a mensagem inicial para integrar somente ao CRM.</p><button type="button" onClick={catalog.reload} className="mt-3 inline-flex min-h-[44px] items-center gap-2 text-sminy-primary"><RefreshCw className="h-4 w-4" />Verificar canal novamente</button></div> : null;
  return (
    <div className="mt-5 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h3 className="text-sm font-semibold">Template do WhatsApp oficial</h3><p className={`mt-1 text-sm ${muted}`}>A primeira mensagem usa um template aprovado da finalidade Formulário.</p></div>
        <button type="button" onClick={catalog.reload} disabled={disabled} className="inline-flex min-h-[44px] items-center gap-2 rounded-xl px-3 text-sm text-sminy-primary transition-colors hover:bg-sminy-primary/10 focus-visible:outline-sminy-primary disabled:opacity-60"><RefreshCw className="h-4 w-4" />Atualizar lista</button>
      </div>
      {catalog.templates.length === 0 ? (
        <div className={`rounded-xl border border-dashed p-5 ${theme.border}`}>
          <FileText className={`mb-2 h-5 w-5 ${muted}`} /><p className="text-sm font-medium">Nenhum template aprovado para formulários</p>
          <p className={`mt-1 text-sm ${muted}`}>Crie um template com finalidade Formulário na biblioteca e aguarde a aprovação da Meta. Ou desative a mensagem inicial para receber os leads somente no CRM.</p>
          <a href="/prompt/templates" className="mt-3 inline-flex min-h-[44px] items-center text-sm font-medium text-sminy-primary">Abrir biblioteca de templates</a>
        </div>
      ) : <label className="block space-y-2"><span className="text-sm font-medium">Template aprovado</span><select className={field} disabled={disabled || !config.send_initial_message} value={selected?.id || ''} onChange={event => onChange({ ...config, official_template_id: Number(event.target.value) || null, official_template_parameters: {} })}>
        <option value="">Selecione um template</option>
        {catalog.templates.map(template => <option key={template.id} value={template.id}>{template.name} · {template.language}</option>)}
      </select></label>}
      {config.official_template_id && !selected && <p role="alert" className="flex items-start gap-2 text-sm text-amber-600"><AlertTriangle className="h-4 w-4 shrink-0" />O template salvo não está disponível ou aprovado para esta clínica. Selecione outro antes de ativar o envio.</p>}
      {selected && <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(240px,0.8fr)]">
        <div className="min-w-0 space-y-4">
          {analysis.errors.map(error => <p key={error} role="alert" className="text-sm text-amber-600">{error}</p>)}
          {analysis.slots.length > 0 ? <h4 className="text-sm font-semibold">Valores da mensagem</h4> : <p className={`text-sm ${muted}`}>Este template não exige variáveis.</p>}
          {analysis.slots.map(slot => {
            const binding = config.official_template_parameters?.[slotKey(slot)];
            const id = `form-template-${slotKey(slot)}`;
            return <div key={id} className={`space-y-2 rounded-xl border p-3 ${theme.border}`}>
              <label className="block text-sm font-medium" htmlFor={`${id}-source`}>{slot.label}</label>
              <select id={`${id}-source`} className={field} disabled={disabled || !config.send_initial_message} value={binding?.source || ''} onChange={event => updateBinding(slot, { source: event.target.value as FormLeadTemplateBinding['source'], value: '' })}>
                <option value="" disabled>Selecione a origem do valor</option>
                {!slot.media && !slot.literalOnly && <><option value="contact_first_name">Primeiro nome do lead</option><option value="contact_full_name">Nome completo do lead</option><option value="clinic_name">Nome da clínica</option><option value="interest">Interesse informado no formulário</option><option value="form_message">Mensagem informada no formulário</option></>}
                <option value="literal">{slot.media ? 'URL HTTPS fixa' : 'Valor fixo'}</option>
              </select>
              {binding?.source === 'literal' && <label className="block space-y-2"><span className={`text-xs ${muted}`}>Valor para {slot.label}</span><input type={slot.media ? 'url' : 'text'} className={field} disabled={disabled || !config.send_initial_message} value={binding.value || ''} maxLength={slot.media ? 2048 : slot.maxLength || 1024} onChange={event => updateBinding(slot, { value: event.target.value })} placeholder={slot.media ? 'https://…' : 'Digite o valor fixo'} /></label>}
              {(binding?.source === 'interest' || binding?.source === 'form_message') && (() => {
                const mappingKey = binding.source === 'interest' ? 'interest' : 'message';
                const savedPath = fieldMapping[mappingKey] || '';
                return <label className="block space-y-2"><span className={`text-xs ${muted}`}>Campo para {binding.source === 'interest' ? 'interesse' : 'mensagem'}</span><select className={field} disabled={disabled || !config.send_initial_message} value={savedPath} onChange={event => onFieldMappingChange({ ...fieldMapping, [mappingKey]: event.target.value })}>
                  <option value="">Selecione um campo do teste recebido</option>
                  {savedPath && !payloadOptions.some(option => option.path === savedPath) && <option value={savedPath}>{savedPath} (campo salvo)</option>}
                  {payloadOptions.map(option => <option key={option.path} value={option.path}>{option.sourceLabel || option.label}</option>)}
                </select></label>;
              })()}
              {slot.media && <p className={`text-xs ${muted}`}>URL pública do arquivo enviado ao lead. A amostra de aprovação do template não é reutilizada como anexo.</p>}
            </div>;
          })}
        </div>
        <div className="min-w-0"><h4 className="mb-3 text-sm font-semibold">Prévia da mensagem</h4>
          {analysis.errors.length === 0 && <WhatsAppTemplatePreview
            clinicId={catalog.clinicId}
            components={selected.components.filter(Boolean).map(component => component.type === 'HEADER' && component.format !== 'TEXT' ? { ...component, example: undefined } : component)}
            bodyValues={Object.fromEntries(analysis.slots.filter(slot => slot.component === 'body').map(slot => [slot.parameter, previewValue(slot)]))}
            headerValues={Object.fromEntries(analysis.slots.filter(slot => slot.component === 'header' && !slot.media).map(slot => [slot.parameter, previewValue(slot)]))}
            mediaName="Anexo do envio (URL configurada)" />}
          <p className={`mt-2 text-xs ${muted}`}>Prévia com nome fictício. As variáveis serão substituídas a cada envio. Salvar não dispara uma mensagem de teste.</p></div>
      </div>}
    </div>
  );
}
